/**
 * Swipe-to-delete for a page row in the sidebar tree.
 *
 * Same gesture standard as the rest of the product (`@/lib/gestures`):
 * recognizer with a deadzone + X axis-lock, rubber-band, haptic at the
 * threshold crossing, and respect for `prefers-reduced-motion` (no exit
 * animation; direct jump). It controls only the horizontal axis: vertical
 * drags yield to native scrolling and the tree's drag-and-drop reordering.
 *
 * Analogous to listto `use-todo-swipe-gestures`, but with a single action
 * (delete), because rename/share already live in other affordances. Deletion
 * delegates to the layout's confirmation flow (cascades to subpages), so the
 * gesture never destroys anything without confirmation.
 */
import { createSignal } from "solid-js";

import { closestSelector, isEditableTarget } from "@/lib/gestures/guards";
import { prefersReducedMotion } from "@/lib/gestures/motion";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { PAGE_ROW_SWIPE } from "@/lib/gestures/presets";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = PAGE_ROW_SWIPE;

interface PageSwipeOptions {
  /** Allow the gesture to begin (e.g. disabled while renaming inline). */
  canSwipe: () => boolean;
  /** Invoked once a left-swipe commits (opens the delete confirmation). */
  onDelete: () => void;
  getSurfaceRef: () => HTMLElement | undefined;
}

export function usePageSwipeGestures(options: PageSwipeOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    canStart: ({ target }) => {
      if (!options.canSwipe()) {
        return false;
      }
      // Ignore starts on controls/links, the rename input, or any area marked as
      // no-swipe.
      if (isEditableTarget(target)) {
        return false;
      }
      return !closestSelector(target, 'button, a, input, [data-no-swipe="true"]');
    },
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      // Allow resistance only toward the left (delete); to the right, the
      // rubber-band already dampens it, but we clamp to 0 so it does not look
      // draggable.
      const next = applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR);
      setTx(Math.min(0, next));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);

      const finalTx = tx();
      if (finalTx < -COMMIT_THRESHOLD_PX) {
        if (prefersReducedMotion()) {
          setTx(0);
          options.onDelete();
          return;
        }
        const width = options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;
        setIsAnimating(true);
        setTx(-width);
        setTimeout(() => {
          options.onDelete();
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
        return;
      }

      // The threshold was not reached: return to center.
      if (prefersReducedMotion()) {
        setTx(0);
        return;
      }
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
    onCancel: () => {
      setIsSwiping(false);
      if (prefersReducedMotion()) {
        setTx(0);
        return;
      }
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
  };
}
